import React from "react";
import { Box, List, ListItem, ListItemText, Typography, Divider } from "@mui/material";
import { useDispatch } from "react-redux";
import { requestComposerAction } from "../../../redux/Slice/Inbox/composerActionsSlice";

// Renders each shortcut as its own small square keycap (split on " + ") instead
// of one wide rectangular pill — a shortcut like "Ctrl + Shift + Y" reads as
// 3 individual keys with a plus between them, same idea as GitHub/Slack's
// shortcut hints (Rahim, 2026-08-24: "instead of using rectangle use square").
const ShortcutKeys = ({ shortcut, onClick }) => {
  const keys = shortcut.split(" + ");
  return (
    <Box
      // Explicit onClick here too — clicking the keycap sits at the far
      // edge of the row and wasn't triggering the action (Rahim, 2026-08-24:
      // "when the A T or N is clicked it doesnt do anything"). Redundant
      // with the row's own onClick when bubbling works, but guarantees it
      // fires regardless.
      onClick={onClick}
      sx={{ display: "flex", alignItems: "center", gap: 0.375, flexShrink: 0, ml: 1 }}
    >
      {keys.map((key, i) => (
        <React.Fragment key={key}>
          {i > 0 && (
            <Typography component="span" sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>
              +
            </Typography>
          )}
          <Box
            component="span"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              // Square keycap for single letters (A/T/N) — Rahim, 2026-08-24:
              // "make it square type". minWidth (not a fixed width) lets a
              // multi-letter modifier key like "Ctrl" widen slightly instead
              // of clipping, while staying square for the common 1-letter case.
              minWidth: 22,
              height: 22,
              px: 0.5,
              fontSize: "0.625rem",
              fontWeight: 500,
              color: theme.palette.text.primary,
              backgroundColor:
                theme.palette.mode === "dark" ? theme.palette.action.selected : theme.palette.grey[200],
              border: `1px solid ${theme.palette.divider}`,
              // Sharper corners than before (was 6px) — reads as square, not
              // a rounded pill.
              borderRadius: "4px",
              whiteSpace: "nowrap",
            })}
          >
            {key}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

const ChatActionBox = ({ setOpenDialog, closeActionBox, platform, isQrConnectedWorkspace }) => {
  const dispatch = useDispatch();
  const isInstagram = platform === "instagram";
  const isFacebook = platform === "facebook";
  // Website chat (chatbot widget) contacts have no template-message equivalent —
  // same exclusion as the Templates/Catalog composer buttons in TextBox.jsx.
  const isWebsite = platform === "website";
  // Phase 6 ("Client UI") QR gating — same underlying "template" dialog TextBox.jsx's own
  // Templates icon button opens; this is a second, independent entry point to it. Instagram/
  // Facebook's own template dialog is unaffected (different channel, not gated by this).
  const templateBlocked = isQrConnectedWorkspace && !isInstagram && !isFacebook;

  // List of actions. "Send template message" fully omitted (not just disabled), 2026-09-26
  // follow-up — same "hide, don't grey out" treatment as TextBox.jsx's own Templates/Catalog
  // icons, since a QR-connected number can never send templates at all.
  const actions = [
    { name: "Close conversation", shortcut: "Ctrl + Shift + Y" },
    { name: "Assign", shortcut: "A" },
    // { name: "Use macro", shortcut: "/" },
    // { name: "Enroll in reusable workflow", shortcut: null },
    ...(isWebsite || templateBlocked ? [] : [{ name: "Send template message", shortcut: "T" }]),
    { name: "Add label", shortcut: "N" },
  ];

  const handleAction = (action) => {
    if (action === "Close conversation") {
      // Was setOpenDialog("close") — nothing in the app ever listened for
      // that value, so this silently did nothing (Rahim, 2026-08-24: "assign
      // and close conversation" not working). The real Close action is local
      // state inside ChatboxNavbar.jsx; composerActionsSlice bridges the two.
      dispatch(requestComposerAction("close"));
    } else if (action === "Assign") {
      // Same dead-state bug as Close — the real Assign dropdown is local
      // state inside ChatboxNavbar.jsx.
      dispatch(requestComposerAction("assign"));
    } else if (action === "Send template message") {
      if (templateBlocked) return;
      setOpenDialog(isInstagram || isFacebook ? "instagramTemplate" : "template");
    } else if (action === "Add label") {
      setOpenDialog("label");
    }
    closeActionBox();
  };

  return (
    <List sx={{ py: 0.5 }}>
      {actions.map((action, index) => {
        return (
          <Box key={action.name}>
            <ListItem
              sx={(theme) => ({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.25,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  cursor: "pointer",
                },
              })}
              onClick={() => handleAction(action.name)}
            >
              <ListItemText
                sx={{ my: 0 }}
                primary={
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.primary" }}>
                    {action.name}
                  </Typography>
                }
              />
              {action.shortcut && (
                <ShortcutKeys shortcut={action.shortcut} onClick={() => handleAction(action.name)} />
              )}
            </ListItem>
            {index < actions.length - 1 && <Divider />}
          </Box>
        );
      })}
    </List>
  );
};

export default ChatActionBox;
